<template>
  <div class="bg-card text-card-foreground flex flex-col gap-6 rounded-xl border py-6 shadow-sm mt-8">
    <!-- Header -->
    <div class="grid auto-rows-min grid-rows-[auto_auto] items-start gap-2 px-6 pb-4">
      <div class="leading-none font-semibold text-lg">Company Details</div>
      <div class="text-muted-foreground text-sm">
        Tell us about your company and what you do
      </div>
    </div>

    <!-- Content -->
    <div class="px-6">
      <div class="grid gap-6">
        <!-- Company Logo -->
        <LogoUploader @error="(msg) => errors.companyLogo = msg" />
        <p v-if="store.logoError" class="text-sm text-red-600 mt-1">{{ store.logoError }}</p>

        <!-- Two-column fields -->
        <div class="grid gap-6 md:grid-cols-2">
          <!-- Industry -->
          <div>
            <label class="block text-sm font-medium mb-1">Industry</label>
            <select
              v-model="store.form.industry"
              @change="handleIndustryChange"
              class="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
              :aria-invalid="!!errors.industry"
            >
              <option value="">Select industry</option>
              <option
                v-for="industry in props.industries"
                :key="industry.id"
                :value="String(industry.id)"
              >
                {{ industry.name }}
              </option>
            </select>
            <p v-if="errors.industry" class="text-sm text-red-600 mt-1">{{ errors.industry }}</p>
          </div>

          <div v-if="subcategoryOptions.length">
            <label class="block text-sm font-medium mb-1">Subcategory <span class="text-muted-foreground">(optional)</span></label>
            <select
              v-model.number="store.form.industrySubcategoryId"
              class="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
              @change="handleSubcategoryChange"
            >
              <option :value="null">Select subcategory</option>
              <option
                v-for="subcategory in subcategoryOptions"
                :key="subcategory.id"
                :value="subcategory.id"
              >
                {{ subcategory.name }}
              </option>
            </select>
          </div>

          <!-- Website -->
          <div>
            <label class="block text-sm font-medium mb-1">Website</label>
            <input
              v-model="store.form.website"
              type="url"
              placeholder="https://example.com"
              class="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
              :aria-invalid="!!errors.website"
            />
            <p v-if="errors.website" class="text-sm text-red-600 mt-1">{{ errors.website }}</p>
          </div>

          <!-- Head Office -->
          <div>
            <label class="block text-sm font-medium mb-1">Head Office</label>
            <input
              v-model="store.form.headOffice"
              type="text"
              placeholder="Perth, Western Australia"
              class="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
              :aria-invalid="!!errors.headOffice"
            />
            <p class="text-xs text-muted-foreground mt-1">Primary office location</p>
            <p v-if="errors.headOffice" class="text-sm text-red-600 mt-1">{{ errors.headOffice }}</p>
          </div>

          <!-- Employee Size -->
          <div>
            <label class="block text-sm font-medium mb-1">Employee Size</label>
            <select
              v-model="store.form.employeeSize"
              class="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
              :aria-invalid="!!errors.employeeSize"
            >
              <option value="">Select size</option>
              <option v-for="size in EMPLOYEE_SIZES" :key="size" :value="size">{{ size }}</option>
            </select>
            <p v-if="errors.employeeSize" class="text-sm text-red-600 mt-1">{{ errors.employeeSize }}</p>
          </div>

          <!-- Company Email -->
          <div class="md:col-span-2">
            <label class="block text-sm font-medium mb-1">Company email <span class="text-muted-foreground">(optional)</span></label>
            <input
              v-model="store.form.contactEmail"
              type="email"
              placeholder="contact@example.com"
              class="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
              :aria-invalid="!!errors.contactEmail"
            />
            <p v-if="errors.contactEmail" class="text-sm text-red-600 mt-1">{{ errors.contactEmail }}</p>
          </div>

          <!-- Company Summary -->
          <div class="md:col-span-2">
            <label class="block text-sm font-medium mb-1">Company Summary</label>
            <textarea
              v-model="store.form.companySummary"
              placeholder="Describe your company's mission, operations, and key activities..."
              class="w-full min-h-32 border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
              :aria-invalid="!!errors.companySummary"
            ></textarea>
            <p class="text-xs text-muted-foreground mt-1">
              A brief overview of your company (2–3 sentences)
            </p>
            <p v-if="errors.companySummary" class="text-sm text-red-600 mt-1">{{ errors.companySummary }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="flex justify-between items-center px-6 pt-6">
      <button
        class="px-4 py-2 rounded-md border border-border text-sm hover:bg-muted"
        @click="$emit('back')"
      >
        Back
      </button>
      <button
        class="px-4 py-2 rounded-md bg-brand text-white text-sm hover:opacity-90"
        @click="validateAndProceed"
      >
        Next
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useCompanyRegistrationStore } from '@/stores/companyRegistrationStore'
import type { CompanyIndustryOption, CompanyIndustrySubcategory } from '@/types/company'

import LogoUploader from './LogoUploader.vue';

const store = useCompanyRegistrationStore()

const props = defineProps<{
  draftId: string
  industries: Array<{ id: number; name: string }>
  companyIndustryOptions: CompanyIndustryOption[]
}>()

const emits = defineEmits(['update', 'next', 'back'])

// const localData = ref({ ...props.data })
const errors = ref<Record<string, string>>({})

const selectedIndustryName = computed(() => {
  const raw = store.form.industry
  if (raw == null || raw === '') return ''

  const asNumber = Number(raw)
  if (!Number.isNaN(asNumber)) {
    const match = props.industries.find((industry) => industry.id === asNumber)
    if (match?.name) return match.name
  }

  return String(raw).trim()
})

const selectedCompanyIndustryOption = computed<CompanyIndustryOption | null>(() => {
  const normalized = selectedIndustryName.value.trim().toLowerCase()
  if (!normalized) return null

  return props.companyIndustryOptions.find((option) => {
    const name = option.name.trim().toLowerCase()
    const slug = option.slug.trim().toLowerCase()
    return normalized === name || normalized === slug || normalized.includes(name) || name.includes(normalized)
  }) ?? null
})

const subcategoryOptions = computed<CompanyIndustrySubcategory[]>(() =>
  selectedCompanyIndustryOption.value?.subcategories ?? []
)

const handleIndustryChange = () => {
  const selected = subcategoryOptions.value.find(
    (subcategory) => subcategory.id === Number(store.form.industrySubcategoryId)
  )
  if (!selected) {
    store.form.industrySubcategoryId = null
    store.form.industrySubcategory = ''
  }
}

const handleSubcategoryChange = () => {
  const selected = subcategoryOptions.value.find(
    (subcategory) => subcategory.id === Number(store.form.industrySubcategoryId)
  )
  store.form.industrySubcategory = selected?.name ?? ''
  if (!selected) {
    store.form.industrySubcategoryId = null
  }
}

const INDUSTRIES = [
  'Mining & Resources',
  'Oil & Gas',
  'Construction',
  'Manufacturing',
  'Technology',
  'Healthcare',
  'Finance',
  'Other',
]

const EMPLOYEE_SIZES = [
  '1-10',
  '11-50',
  '51-200',
  '201-500',
  '501-1000',
  '1001-5000',
  '5001-10000',
  '10000+',
]

// watch(localData, (val) => emits('update', val), { deep: true })

// Validation now uses store.form directly
const validateAndProceed = () => {
  const f = store.form
  const newErrors: Record<string, string> = {}

  if (!f.companyName.trim()) newErrors.companyName = 'Company name is required'
  if (!f.industry) newErrors.industry = 'Please select an industry'
  if (!f.website.trim()) newErrors.website = 'Website is required'
  else if (!/^https?:\/\/.+/.test(f.website)) newErrors.website = 'Please enter a valid URL (e.g., https://example.com)'
  if (!f.headOffice.trim()) newErrors.headOffice = 'Head office location is required'
  if (!f.employeeSize) newErrors.employeeSize = 'Please select employee size'
  if (f.contactEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.contactEmail)) {
    newErrors.contactEmail = 'Please enter a valid email address'
  } else if (f.contactEmail.trim().length > 255) {
    newErrors.contactEmail = 'Company email must be 255 characters or fewer'
  }
  if (!f.companySummary.trim()) newErrors.companySummary = 'Company summary is required'

  errors.value = newErrors
  if (Object.keys(newErrors).length === 0) emits('next')
}
</script>
